import type { NodeOutput } from "@skaile/workspaces/types";
import { evaluateFlowExpression, FlowExpressionError } from "../contract/expression.js";
import { buildFlowExpressionContext } from "./bindings.js";
import { isRouterNode } from "./node-shape.js";
import type { FlowDefinition, FlowNode, NodeExecutionSnapshot } from "./types.js";

const ROUTER_SELECTION_MARKER = "\n\n[skaile:flow-router-selection:";

/** One deterministic router choice being settled in the current atomic batch. */
export interface RouterSettlement {
  routerNodeId: string;
  selectedTarget: string | null;
}

/** Internal result of evaluating one already-admitted router. */
export type RouterSelection =
  | { selectedTarget: string | null }
  | { unresolved: "not-ready" | "no-match" };

function pendingProducerOutput(
  flow: FlowDefinition,
  snapshots: Map<string, NodeExecutionSnapshot>,
  missingPath: string | undefined,
): boolean {
  if (!missingPath) return false;
  const segments = missingPath.split(".");
  if (
    segments.length < 5 ||
    segments[0] !== "nodes" ||
    segments[2] !== "output" ||
    segments[3] !== "fields"
  ) {
    return false;
  }
  const producer = flow.nodes.find(({ id }) => id === segments[1]);
  if (!producer?.contract?.output?.fields) return false;
  // Only a producer that can still produce is worth waiting for — a terminal
  // one never will, and calling it "pending" parks the router forever.
  return !["complete", "skipped", "failed"].includes(
    snapshots.get(producer.id)?.status ?? "not_started",
  );
}

/** Evaluate ordered routes for a router whose availability was already established. */
export function evaluateRouterSelection(
  flow: FlowDefinition,
  router: FlowNode,
  snapshots: Map<string, NodeExecutionSnapshot>,
): RouterSelection {
  if (!isRouterNode(router)) return { unresolved: "no-match" };
  const context = buildFlowExpressionContext(flow, snapshots);
  for (const route of router.run.routes) {
    try {
      if (evaluateFlowExpression(route.when, context)) {
        return { selectedTarget: route.target };
      }
    } catch (error) {
      if (
        error instanceof FlowExpressionError &&
        pendingProducerOutput(flow, snapshots, error.missingPath)
      ) {
        return { unresolved: "not-ready" };
      }
      throw error;
    }
  }
  return { unresolved: "no-match" };
}

/** Encode a router choice in the existing durable node-output envelope. */
export function routerSelectionOutput(selectedTarget: string | null): NodeOutput {
  const summary =
    selectedTarget === null
      ? "Router completed without selecting a target."
      : `Router selected ${selectedTarget}.`;
  return {
    summary: `${summary}${ROUTER_SELECTION_MARKER}${JSON.stringify(selectedTarget)}]`,
    artifacts: [],
  };
}

function routeTargets(router: FlowNode): string[] {
  if (!isRouterNode(router)) return [];
  return [...new Set(router.run.routes.flatMap(({ target }) => (target === null ? [] : [target])))];
}

interface RouterOwnershipIndex {
  routers: FlowNode[];
  outgoing: Map<string, string[]>;
}

/** Index router-to-router ownership through route targets and hard-flow descendants. */
function routerOwnershipIndex(
  flow: FlowDefinition,
  snapshots: Map<string, NodeExecutionSnapshot>,
): RouterOwnershipIndex {
  const routers = flow.nodes.filter(
    (node) =>
      isRouterNode(node) &&
      !["complete", "failed", "skipped"].includes(snapshots.get(node.id)?.status ?? "not_started"),
  );
  const routerIds = new Set(routers.map(({ id }) => id));
  const hardOutgoing = new Map<string, string[]>();
  for (const edge of flow.edges) {
    if (edge.type !== "flow") continue;
    const targets = hardOutgoing.get(edge.source) ?? [];
    targets.push(edge.target);
    hardOutgoing.set(edge.source, targets);
  }

  const outgoing = new Map<string, string[]>();
  for (const router of routers) {
    const owned = new Set<string>();
    const visited = new Set<string>();
    const queue = routeTargets(router);
    for (let index = 0; index < queue.length; index++) {
      const nodeId = queue[index];
      if (visited.has(nodeId)) continue;
      visited.add(nodeId);
      if (routerIds.has(nodeId)) owned.add(nodeId);
      queue.push(...(hardOutgoing.get(nodeId) ?? []));
    }
    outgoing.set(router.id, [...owned]);
  }
  return { routers, outgoing };
}

/**
 * Return every member of each source router-ownership SCC.
 *
 * Route ownership is deliberately not a dependency arc and may cycle even though hard flow is
 * acyclic. Source SCC members are ungated so normal hard-edge/precondition admission and ordered
 * decidability select the settlement frontier. Ordinary parent components remain ahead of nested
 * children, while self-targeting and mutually targeting routers cannot deadlock admission. The
 * graph is preindexed and both SCC passes are iterative.
 */
function routerOwnershipSourceComponents(
  flow: FlowDefinition,
  snapshots: Map<string, NodeExecutionSnapshot>,
): string[][] {
  const { routers, outgoing } = routerOwnershipIndex(flow, snapshots);
  const reverse = new Map<string, string[]>();
  for (const router of routers) reverse.set(router.id, []);
  for (const [source, targets] of outgoing) {
    for (const target of targets) reverse.get(target)?.push(source);
  }

  const visited = new Set<string>();
  const finished: string[] = [];
  for (const router of routers) {
    if (visited.has(router.id)) continue;
    const stack: Array<{ nodeId: string; exiting: boolean }> = [
      { nodeId: router.id, exiting: false },
    ];
    while (stack.length > 0) {
      const frame = stack.pop();
      if (!frame) break;
      if (frame.exiting) {
        finished.push(frame.nodeId);
        continue;
      }
      if (visited.has(frame.nodeId)) continue;
      visited.add(frame.nodeId);
      stack.push({ nodeId: frame.nodeId, exiting: true });
      const targets = outgoing.get(frame.nodeId) ?? [];
      for (let index = targets.length - 1; index >= 0; index--) {
        if (!visited.has(targets[index])) {
          stack.push({ nodeId: targets[index], exiting: false });
        }
      }
    }
  }

  const componentByRouter = new Map<string, number>();
  const components: string[][] = [];
  for (let index = finished.length - 1; index >= 0; index--) {
    const start = finished[index];
    if (componentByRouter.has(start)) continue;
    const componentId = components.length;
    const component: string[] = [];
    const stack = [start];
    componentByRouter.set(start, componentId);
    while (stack.length > 0) {
      const nodeId = stack.pop();
      if (!nodeId) break;
      component.push(nodeId);
      for (const source of reverse.get(nodeId) ?? []) {
        if (componentByRouter.has(source)) continue;
        componentByRouter.set(source, componentId);
        stack.push(source);
      }
    }
    components.push(component);
  }

  const incomingComponents = new Set<number>();
  for (const [source, targets] of outgoing) {
    const sourceComponent = componentByRouter.get(source);
    for (const target of targets) {
      const targetComponent = componentByRouter.get(target);
      if (
        sourceComponent !== undefined &&
        targetComponent !== undefined &&
        sourceComponent !== targetComponent
      ) {
        incomingComponents.add(targetComponent);
      }
    }
  }

  const definitionOrder = new Map(flow.nodes.map((node, index) => [node.id, index]));
  const sourceComponents = components.filter(
    (_, componentId) => !incomingComponents.has(componentId),
  );
  for (const component of sourceComponents) {
    component.sort(
      (left, right) =>
        (definitionOrder.get(left) ?? Number.MAX_SAFE_INTEGER) -
        (definitionOrder.get(right) ?? Number.MAX_SAFE_INTEGER),
    );
  }
  return sourceComponents.sort(
    (left, right) =>
      (definitionOrder.get(left[0]) ?? Number.MAX_SAFE_INTEGER) -
      (definitionOrder.get(right[0]) ?? Number.MAX_SAFE_INTEGER),
  );
}

/** Decode and validate a completed router's engine-owned selection summary. */
export function persistedRouterSelection(
  router: FlowNode,
  snapshot: NodeExecutionSnapshot | undefined,
): { selectedTarget: string | null } | undefined {
  if (!isRouterNode(router) || snapshot?.status !== "complete") return undefined;
  const summary = snapshot.output?.summary;
  const markerIndex = summary?.lastIndexOf(ROUTER_SELECTION_MARKER) ?? -1;
  if (!summary || markerIndex < 0 || !summary.endsWith("]")) return undefined;
  let selectedTarget: unknown;
  try {
    selectedTarget = JSON.parse(summary.slice(markerIndex + ROUTER_SELECTION_MARKER.length, -1));
  } catch {
    return undefined;
  }
  if (
    selectedTarget !== null &&
    (typeof selectedTarget !== "string" || !routeTargets(router).includes(selectedTarget))
  ) {
    return undefined;
  }
  if (selectedTarget === null && !router.run.routes.some(({ target }) => target === null)) {
    return undefined;
  }
  return summary === routerSelectionOutput(selectedTarget).summary ? { selectedTarget } : undefined;
}

function edgeKey(source: string, target: string): string {
  return `${source}\u0000${target}`;
}

/**
 * Plan exclusive branch skips for all known router choices as one coordinated settlement.
 *
 * Only hard `flow` edges participate. The graph is indexed once, then each distinct losing
 * target performs a linear adjacency walk; optional and parallel edges can neither spread nor
 * preserve exclusivity. A selected route, a still-live unsettled router, shared convergence, or
 * an incoming hard predecessor outside the losing region protects that node and its descendants.
 */
export function planRouterSkips(
  flow: FlowDefinition,
  snapshots: Map<string, NodeExecutionSnapshot>,
  settlements: readonly RouterSettlement[],
): string[] {
  const routers = flow.nodes.filter(isRouterNode);
  const currentSelections = new Map(
    settlements.map(({ routerNodeId, selectedTarget }) => [routerNodeId, selectedTarget]),
  );
  const knownSelections = new Map<string, string | null>();
  for (const router of routers) {
    if (currentSelections.has(router.id)) {
      knownSelections.set(router.id, currentSelections.get(router.id) ?? null);
      continue;
    }
    const persisted = persistedRouterSelection(router, snapshots.get(router.id));
    if (persisted) knownSelections.set(router.id, persisted.selectedTarget);
  }

  const outgoing = new Map<string, string[]>();
  const incoming = new Map<string, string[]>();
  for (const edge of flow.edges) {
    if (edge.type !== "flow") continue;
    const targets = outgoing.get(edge.source) ?? [];
    targets.push(edge.target);
    outgoing.set(edge.source, targets);
    const sources = incoming.get(edge.target) ?? [];
    sources.push(edge.source);
    incoming.set(edge.target, sources);
  }

  const losingRoots = new Set<string>();
  const selectedRoots = new Set<string>();
  const losingRouteEdges = new Set<string>();
  for (const router of routers) {
    if (!knownSelections.has(router.id)) continue;
    const selectedTarget = knownSelections.get(router.id) ?? null;
    if (selectedTarget !== null) selectedRoots.add(selectedTarget);
    for (const target of routeTargets(router)) {
      if (target === selectedTarget) continue;
      losingRoots.add(target);
      losingRouteEdges.add(edgeKey(router.id, target));
    }
  }

  const candidateOrigins = new Map<string, Set<string>>();
  for (const root of losingRoots) {
    const visited = new Set<string>();
    const queue = [root];
    for (let index = 0; index < queue.length; index++) {
      const nodeId = queue[index];
      if (visited.has(nodeId)) continue;
      visited.add(nodeId);
      const origins = candidateOrigins.get(nodeId) ?? new Set<string>();
      origins.add(root);
      candidateOrigins.set(nodeId, origins);
      for (const target of outgoing.get(nodeId) ?? []) queue.push(target);
    }
  }
  const candidates = new Set(candidateOrigins.keys());

  const protectionQueue = [...selectedRoots];
  for (const [nodeId, origins] of candidateOrigins) {
    if (origins.size >= 2) protectionQueue.push(nodeId);
    for (const source of incoming.get(nodeId) ?? []) {
      if (candidates.has(source)) continue;
      if (losingRouteEdges.has(edgeKey(source, nodeId))) continue;
      if (["failed", "skipped"].includes(snapshots.get(source)?.status ?? "not_started")) continue;
      protectionQueue.push(nodeId);
      break;
    }
  }

  const unknownSelections = new Map<string, string[]>();
  for (const router of routers) {
    if (knownSelections.has(router.id)) continue;
    const status = snapshots.get(router.id)?.status ?? "not_started";
    if (status === "failed" || status === "skipped") continue;
    const targets = routeTargets(router);
    unknownSelections.set(router.id, targets);
    if (!candidates.has(router.id)) protectionQueue.push(...targets);
  }

  const protectedNodes = new Set<string>();
  for (let index = 0; index < protectionQueue.length; index++) {
    const nodeId = protectionQueue[index];
    if (protectedNodes.has(nodeId)) continue;
    protectedNodes.add(nodeId);
    protectionQueue.push(...(outgoing.get(nodeId) ?? []));
    protectionQueue.push(...(unknownSelections.get(nodeId) ?? []));
  }

  return flow.nodes.flatMap((node) =>
    candidates.has(node.id) && !protectedNodes.has(node.id) ? [node.id] : [],
  );
}

/**
 * Return every possible target branch gated by an unsettled router.
 *
 * Gating follows hard `flow` descendants only. A selected route, shared convergence, or another
 * live hard predecessor protects a node, matching settlement pruning without mutating snapshots.
 */
export function unsettledRouterBranchIds(
  flow: FlowDefinition,
  snapshots: Map<string, NodeExecutionSnapshot>,
): Set<string> {
  const unsettled = flow.nodes.filter(
    (node) =>
      isRouterNode(node) &&
      !["complete", "failed", "skipped"].includes(snapshots.get(node.id)?.status ?? "not_started"),
  );
  const branches = new Set(
    planRouterSkips(
      flow,
      snapshots,
      unsettled.map(({ id }) => ({ routerNodeId: id, selectedTarget: null })),
    ),
  );
  for (const component of routerOwnershipSourceComponents(flow, snapshots)) {
    for (const routerId of component) branches.delete(routerId);
  }
  return branches;
}

/**
 * Return one actually available, decidable router per source ownership component.
 *
 * Every source-component member remains ungated for normal admission. Within a settlement wave,
 * definition order chooses the first member that is both available and decidable; unresolved or
 * blocked peers cannot starve it. Limiting each component to one choice prevents a router from
 * completing atomically after that same wave makes its branch losing. Independent source
 * components can still settle together.
 */
export function availableRouterFrontier(
  flow: FlowDefinition,
  snapshots: Map<string, NodeExecutionSnapshot>,
): FlowNode[] {
  const nodesById = new Map(flow.nodes.map((node) => [node.id, node]));
  return routerOwnershipSourceComponents(flow, snapshots).flatMap((component) => {
    for (const routerId of component) {
      const node = nodesById.get(routerId);
      if (!node || snapshots.get(routerId)?.status !== "available") continue;
      if ("unresolved" in evaluateRouterSelection(flow, node, snapshots)) continue;
      return [node];
    }
    return [];
  });
}
